import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { CronJob } from '../../types/cron.js';

function formatSchedule(schedule: string): string {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length < 5) return schedule;

  const [min, hour, dom, mon, dow] = parts;

  if (dom === '*' && mon === '*' && dow === '*' && hour !== '*' && min !== '*') {
    return `Taeglich um ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  if (dom === '*' && mon === '*' && dow !== '*' && hour !== '*' && min !== '*') {
    const days: Record<string, string> = { '0': 'So', '1': 'Mo', '2': 'Di', '3': 'Mi', '4': 'Do', '5': 'Fr', '6': 'Sa', '7': 'So' };
    const dayLabel = dow.split(',').map(d => days[d] ?? d).join(', ');
    return `${dayLabel} um ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  if (min !== '*' && hour !== '*' && dom !== '*' && mon === '*') {
    return `Am ${dom}. jeden Monats um ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  if (min.startsWith('*/') || (hour === '*' && min !== '*')) {
    const interval = min.replace('*/', '');
    return `Alle ${interval} Minuten`;
  }
  return schedule;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '\u2014';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

@customElement('cp-cron-job-list')
export class CpCronJobList extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) jobs: CronJob[] = [];

  private _onToggle(job: CronJob): void {
    this.dispatchEvent(new CustomEvent('job-toggle', { detail: { jobId: job.id }, bubbles: true, composed: true }));
  }

  private _onTrigger(job: CronJob): void {
    this.dispatchEvent(new CustomEvent('job-trigger', { detail: { jobId: job.id }, bubbles: true, composed: true }));
  }

  private _onDelete(job: CronJob): void {
    this.dispatchEvent(new CustomEvent('job-delete', { detail: { jobId: job.id, jobName: job.name }, bubbles: true, composed: true }));
  }

  private _onEdit(job: CronJob): void {
    this.dispatchEvent(new CustomEvent('job-edit', { detail: { job }, bubbles: true, composed: true }));
  }

  private _onShowExecutions(job: CronJob): void {
    this.dispatchEvent(new CustomEvent('job-show-executions', { detail: { job }, bubbles: true, composed: true }));
  }

  render() {
    if (this.jobs.length === 0) return nothing;

    return html`
      <div class="cp-cron-job-list">
        <table class="cp-cron-job-list__table">
          <thead>
            <tr>
              <th class="cp-cron-job-list__th">Name</th>
              <th class="cp-cron-job-list__th">Schedule</th>
              <th class="cp-cron-job-list__th">Status</th>
              <th class="cp-cron-job-list__th">Letzte Ausfuehrung</th>
              <th class="cp-cron-job-list__th cp-cron-job-list__th--actions">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            ${this.jobs.map(job => this._renderRow(job))}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderRow(job: CronJob) {
    const enabled = job.enabled === 1;
    return html`
      <tr class="cp-cron-job-list__row ${enabled ? '' : 'cp-cron-job-list__row--disabled'}">
        <td class="cp-cron-job-list__td">
          <div class="cp-cron-job-list__name">${job.name}</div>
          ${job.agent_name ? html`
            <div class="cp-cron-job-list__agent">
              <span class="cp-cron-job-list__agent-dot" style="background-color: ${job.agent_color || 'var(--color-bg-tertiary)'}"></span>
              <span class="cp-cron-job-list__agent-name">${job.agent_name}</span>
            </div>
          ` : nothing}
          ${job.description ? html`<div class="cp-cron-job-list__desc">${job.description}</div>` : nothing}
          ${job.skill_ids?.length ? html`
            <div class="cp-cron-job-list__skills">
              ${job.skill_ids.map(id => html`<span class="cp-cron-job-list__skill-tag">${id}</span>`)}
            </div>
          ` : nothing}
        </td>
        <td class="cp-cron-job-list__td">
          <span class="cp-cron-job-list__schedule">${formatSchedule(job.schedule)}</span>
          <span class="cp-cron-job-list__cron-expr">${job.schedule}</span>
        </td>
        <td class="cp-cron-job-list__td">
          <button
            class="cp-cron-job-list__toggle ${enabled ? 'cp-cron-job-list__toggle--on' : ''}"
            @click=${() => this._onToggle(job)}
            title="${enabled ? 'Deaktivieren' : 'Aktivieren'}"
          >
            <span class="cp-cron-job-list__toggle-knob"></span>
          </button>
        </td>
        <td class="cp-cron-job-list__td">
          <span class="cp-cron-job-list__last-run">${formatDate(job.last_run)}</span>
        </td>
        <td class="cp-cron-job-list__td cp-cron-job-list__td--actions">
          <button
            class="cp-cron-job-list__action-btn cp-cron-job-list__action-btn--trigger"
            @click=${() => this._onTrigger(job)}
            title="Jetzt ausfuehren"
          >&#9654;</button>
          <button
            class="cp-cron-job-list__action-btn cp-cron-job-list__action-btn--edit"
            @click=${() => this._onEdit(job)}
            title="Bearbeiten"
          >&#9998;</button>
          <button
            class="cp-cron-job-list__action-btn cp-cron-job-list__action-btn--log"
            @click=${() => this._onShowExecutions(job)}
            title="Ausfuehrungen anzeigen"
          >&#9776;</button>
          <button
            class="cp-cron-job-list__action-btn cp-cron-job-list__action-btn--delete"
            @click=${() => this._onDelete(job)}
            title="Loeschen"
          >&#10005;</button>
        </td>
      </tr>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cron-job-list': CpCronJobList;
  }
}
